<template>
  <div>
    <section class="relative overflow-hidden bg-slate-950 text-white">
      <div class="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(0,177,148,0.4),_transparent_55%)]" />
      <div class="relative mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-28">
        <p class="text-sm font-medium uppercase tracking-widest text-primary-300">Platformă e-commerce</p>
        <h1 class="mt-4 max-w-3xl text-4xl font-semibold tracking-tight sm:text-6xl">
          Magazinul tău online, gata de vânzare în câteva minute.
        </h1>
        <p class="mt-6 max-w-2xl text-lg text-slate-300">
          Novartshop e platforma pe care îți construiești shop-ul: catalog, comenzi, plăți, livrări, CMS și marketing.
          {{ trialLabel }}
        </p>
        <div class="mt-8 flex flex-wrap gap-3">
          <UButton to="/start" size="lg" :label="heroCta" />
          <UButton to="/preturi" size="lg" variant="outline" color="neutral" label="Vezi prețurile" class="bg-white/5 text-white ring-white/20" />
        </div>
      </div>
    </section>

    <section v-if="shops.length" id="magazine" class="border-b border-slate-200 bg-white py-20">
      <div class="mx-auto max-w-6xl px-4 sm:px-6">
        <h2 class="text-3xl font-semibold tracking-tight">Magazine pe Novartshop</h2>
        <p class="mt-3 max-w-2xl text-slate-600">
          Shop-uri live construite pe platformă. Intră pe oricare — sau pornește-l pe al tău.
        </p>
        <div class="mt-10">
          <MarketingShopShowcase :shops="shops" />
        </div>
      </div>
    </section>

    <section id="functii" class="mx-auto max-w-6xl px-4 py-20 sm:px-6">
      <h2 class="text-3xl font-semibold tracking-tight">Tot ce are nevoie un magazin</h2>
      <p class="mt-3 max-w-2xl text-slate-600">
        Un shop pe Novartshop nu e o temă. E magazin complet: catalog, comenzi, CMS, marketing și integrările pe care le folosești deja în România — le activezi din admin, cu cheile tale.
      </p>
      <div class="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        <article v-for="item in features" :key="item.title" class="rounded-2xl border border-slate-200 bg-white p-5">
          <div class="flex size-10 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
            <UIcon :name="item.icon" class="size-5" />
          </div>
          <h3 class="mt-4 font-semibold">{{ item.title }}</h3>
          <p class="mt-2 text-sm leading-6 text-slate-600">{{ item.body }}</p>
        </article>
      </div>

      <div class="mt-16 rounded-3xl border border-slate-200 bg-slate-50 p-6 sm:p-8">
        <h3 class="text-xl font-semibold tracking-tight">Integrări gata de conectat</h3>
        <!-- <p class="mt-2 max-w-2xl text-slate-600">
          Toate stau în Admin → Integrări. Checkout-ul magazinului e separat de Stripe-ul de abonament al platformei.
        </p> -->
        <div class="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <div v-for="group in integrations" :key="group.title">
            <p class="text-xs font-semibold uppercase tracking-wider text-primary-600">{{ group.title }}</p>
            <ul class="mt-3 flex flex-wrap gap-2">
              <li
                v-for="name in group.items"
                :key="name"
                class="rounded-full border border-slate-200 bg-white px-3 py-1 text-sm text-slate-700"
              >
                {{ name }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <section class="bg-white py-20">
      <div class="mx-auto max-w-6xl px-4 sm:px-6">
        <h2 class="text-3xl font-semibold tracking-tight">Alege planul</h2>
        <p class="mt-3 text-slate-600">
          Adaugi cardul la înscriere. {{ plans?.trialDays || 14 }} zile sunt 0 lei.
        </p>
        <div class="mt-10">
          <USkeleton v-if="loading && !plans" class="h-64 rounded-2xl" />
          <MarketingPlanCards v-else :plans="plans" />
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
const { plans, loading, fetchPlans } = usePlatformPlans()
const { shops, fetchShops } = usePlatformShops()

const trialLabel = computed(() => {
  const days = plans.value?.trialDays || 14
  return `Începi cu ${days} zile trial, 0 lei.`
})

const heroCta = computed(() => {
  const days = plans.value?.trialDays || 14
  return `Creează magazinul — ${days} zile la 0 lei`
})

const features = [
  {
    icon: 'i-heroicons-cube',
    title: 'Catalog',
    body: 'Produse, categorii, atribute, variante, câmpuri complexe, stoc, imagini și SEO. Import din WooCommerce, XML furnizor. AI (ChatGPT / Gemini) pentru descrieri.',
  },
  {
    icon: 'i-heroicons-shopping-bag',
    title: 'Comenzi și checkout',
    body: 'Coș, cupoane, comparare, wishlist, comenzi manuale din admin, statusuri și istoric. Ramburs, transfer, card sau rate. Pași extra la checkout (task-uri) și ridicare din magazin.',
  },
  {
    icon: 'i-heroicons-credit-card',
    title: 'Plăți clienți',
    body: 'Netopia, Stripe, TBI Rate, ramburs și transfer bancar — din Integrări, pe magazinul tău.',
  },
  {
    icon: 'i-heroicons-truck',
    title: 'Livrări și facturi',
    body: 'Colete Online: oferte la checkout și AWB din comandă. Transport procentual sau ridicare din magazin. Facturi SmartBill sau Oblio, cu descărcare din gestiune.',
  },
  {
    icon: 'i-heroicons-bell',
    title: 'Notificări',
    body: 'Email pe SMTP-ul tău, SMS prin SMSAPI sau SMSAdvert, WhatsApp Cloud API pe evenimente de comandă. Chat live tawk.to pe storefront.',
  },
  {
    icon: 'i-heroicons-megaphone',
    title: 'Marketing și ads',
    body: 'Bannere, butoane CTA, campanii email, recenzii pe produs și Google Reviews. Meta Pixel + Conversions API, Google Analytics, Tag Manager și feed Google Merchant.',
  },
  {
    icon: 'i-heroicons-document-text',
    title: 'CMS și aspect',
    body: 'Pagini legale și conținut, homepage, temă, logo-uri, sigle ANPC/plăți. PWA pe telefon și notificări push pentru comenzi noi.',
  },
  {
    icon: 'i-heroicons-users',
    title: 'Conturi clienți',
    body: 'Login cu email, Google, Facebook, Apple și passkeys. Roluri în admin (admin, manager, client). Wishlist, comenzi și date în contul clientului.',
  },
  {
    icon: 'i-heroicons-banknotes',
    title: 'Vinde-ne produsul',
    body: 'Buyback: reguli de evaluare, ofertă pe loc și solicitări de la clienți. Opțional, servicii cu preț pe unitate (mp, oră) la checkout.',
  },
  {
    icon: 'i-heroicons-globe-alt',
    title: 'Domeniul tău',
    body: 'Pornești imediat pe /slug și pe slug.novartshop.ro. Când vrei, leagă un domeniu propriu. Fiecare magazin are admin, setări și integrări separate.',
  },
]

const integrations = [
  { title: 'Plăți', items: ['Netopia', 'Stripe', 'TBI Rate', 'Ramburs', 'Transfer bancar'] },
  { title: 'Transport', items: ['Colete Online', 'Transport procentual', 'Ridicare din magazin'] },
  { title: 'Facturare', items: ['SmartBill', 'Oblio'] },
  { title: 'Notificări', items: ['SMTP', 'SMSAPI', 'SMSAdvert', 'WhatsApp', 'tawk.to'] },
  { title: 'Ads și SEO', items: ['Meta Pixel + CAPI', 'Google Analytics', 'Google Tag Manager', 'Google Merchant'] },
  { title: 'Conturi și AI', items: ['Google', 'Facebook', 'Apple', 'Passkeys', 'ChatGPT', 'Gemini'] },
]

useHead({
  title: 'Novartshop — magazinul tău online',
  meta: [
    { name: 'description', content: 'Creează un magazin online pe Novartshop. Catalog, comenzi, plăți, livrări. 14 zile trial la 0 lei, cu card de la start.' },
  ],
})

await Promise.all([fetchPlans(), fetchShops()])
</script>
